import React, { useEffect, useState, useCallback } from 'react';
import {
  Badge, IconButton, Popover, Box, Typography, List, ListItem, ListItemText,
  ListItemIcon, Stack, Chip, Button, Divider, Tooltip,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiServices';

const TYPE_CONFIG = {
  critical: { color: 'error',   icon: <ErrorIcon />,   label: 'Kritik' },
  high:     { color: 'error',   icon: <WarningIcon />, label: 'I Lartë' },
  medium:   { color: 'warning', icon: <WarningIcon />, label: 'Mesatar' },
  low:      { color: 'info',    icon: <InfoIcon />,    label: 'Info' },
};

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('sq-AL', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl]           = useState(null);
  const open = Boolean(anchorEl);

  const load = useCallback(() => {
    apiService().query('getNotifications', '').then(r => {
      if (r?.success) setNotifications(r.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  const unreadCount = notifications.filter(n => !n.IsRead).length;

  const markAsRead = async (n) => {
    if (n.IsRead) return;
    const updated = { ...n, IsRead: true };
    await apiService().put('notifications', updated);
    setNotifications(prev => prev.map(x => Number(x.Id) === Number(n.Id) ? updated : x));
  };

  const markAllRead = async () => {
    const unread = notifications.filter(x => !x.IsRead);
    await Promise.all(unread.map(n => apiService().put('notifications', { ...n, IsRead: true })));
    setNotifications(prev => prev.map(x => ({ ...x, IsRead: true })));
  };

  const critical = notifications.filter(n => n.Type === 'critical' && !n.IsRead);
  const high     = notifications.filter(n => n.Type === 'high'     && !n.IsRead);

  return (
    <>
      <Tooltip title={`${unreadCount} njoftim(e) të palexuara`}>
        <IconButton
          color="inherit"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="notifications"
        >
          <Badge
            badgeContent={unreadCount}
            color={critical.length > 0 ? 'error' : high.length > 0 ? 'warning' : 'info'}
            max={99}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxHeight: 520, overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={700}>
              Njoftimet
              {unreadCount > 0 && (
                <Chip size="small" label={unreadCount} color="error" sx={{ ml: 1 }} />
              )}
            </Typography>
            {unreadCount > 0 && (
              <Button size="small" startIcon={<DoneAllIcon />} onClick={markAllRead}>
                Shëno të gjitha
              </Button>
            )}
          </Stack>
        </Box>

        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {notifications.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Asnjë njoftim aktiv. Sistemi është i rregullt!
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {notifications.map((n, i) => {
                const cfg  = TYPE_CONFIG[n.Type] || TYPE_CONFIG.low;
                const Icon = React.cloneElement(cfg.icon, { fontSize: 'small', color: cfg.color });
                return (
                  <React.Fragment key={n.Id}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        bgcolor: n.IsRead ? 'transparent' : 'action.hover',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.selected' },
                        pr: 1,
                      }}
                      onClick={() => markAsRead(n)}
                      component={n.Link ? Link : 'li'}
                      to={n.Link}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        {Icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" fontWeight={n.IsRead ? 500 : 700} sx={{ flex: 1 }}>
                              {n.Title}
                            </Typography>
                            <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" sx={{ fontSize: 9, height: 18 }} />
                          </Stack>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {n.Message}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {fmt(n.CreatedAt)}
                            </Typography>
                          </Box>
                        }
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                    {i < notifications.length - 1 && <Divider sx={{ ml: 5 }} />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {notifications.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Button
              size="small"
              component={Link}
              to="/audit-log"
              onClick={() => setAnchorEl(null)}
            >
              Shiko Audit Log
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
}
